import { useEffect, useMemo, useState } from 'react';
import { Bell, Save, Mail } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { supabase } from '../../lib/supabase';
import {
  REMINDERS,
  remindersForRole,
  LEAD_DAY_OPTIONS,
  HOUR_OPTIONS,
  DOW_OPTIONS,
  DOM_OPTIONS,
  formatHour,
  type ReminderDef,
} from '../../config/emailReminders';

interface Pref {
  enabled: boolean;
  lead_days: number | null;
  schedule_config: Record<string, unknown> | null;
}

function defaultPref(r: ReminderDef): Pref {
  return {
    enabled: true,
    lead_days: r.control === 'expiring' ? r.defaultLeadDays ?? 30 : null,
    schedule_config:
      r.control === 'weekly' || r.control === 'monthly' ? { ...(r.defaultSchedule ?? {}) } : null,
  };
}

export default function EmailRemindersView() {
  const { user, profile } = useAuth();
  const toast = useToast();
  const [prefs, setPrefs] = useState<Record<string, Pref>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const visible = useMemo(() => remindersForRole(profile?.role), [profile?.role]);
  const categories = useMemo(() => {
    const seen: string[] = [];
    for (const r of visible) if (!seen.includes(r.category)) seen.push(r.category);
    return seen;
  }, [visible]);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!user) return;
      setLoading(true);
      // 1) defaults (everything on) then 2) overlay saved rows.
      const base: Record<string, Pref> = {};
      for (const r of REMINDERS) base[r.key] = defaultPref(r);
      const { data } = await supabase
        .from('email_reminder_preferences')
        .select('reminder_key, enabled, lead_days, schedule_config')
        .eq('user_id', user.id);
      for (const row of data || []) {
        if (base[row.reminder_key]) {
          base[row.reminder_key] = {
            enabled: row.enabled,
            lead_days: row.lead_days,
            schedule_config: row.schedule_config,
          };
        }
      }
      if (active) {
        setPrefs(base);
        setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [user, profile?.role]);

  const patch = (key: string, p: Partial<Pref>) =>
    setPrefs((prev) => ({ ...prev, [key]: { ...prev[key], ...p } }));

  const setCategoryEnabled = (category: string, enabled: boolean) =>
    setPrefs((prev) => {
      const next = { ...prev };
      for (const r of visible.filter((x) => x.category === category)) {
        next[r.key] = { ...next[r.key], enabled };
      }
      return next;
    });

  const save = async () => {
    if (!user) return;
    setSaving(true);
    try {
      const rows = visible.map((r) => {
        const p = prefs[r.key] ?? defaultPref(r);
        return {
          user_id: user.id,
          reminder_key: r.key,
          enabled: p.enabled,
          lead_days: r.control === 'expiring' ? p.lead_days ?? r.defaultLeadDays ?? 30 : null,
          schedule_config:
            r.control === 'weekly' || r.control === 'monthly'
              ? p.schedule_config ?? r.defaultSchedule ?? null
              : null,
          updated_at: new Date().toISOString(),
        };
      });
      const { error } = await supabase
        .from('email_reminder_preferences')
        .upsert(rows, { onConflict: 'user_id,reminder_key' });
      if (error) throw error;
      toast.success('Reminder preferences saved');
    } catch (err) {
      console.error('Error saving reminder preferences:', err);
      toast.error('Failed to save reminder preferences');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-slate-200 rounded w-1/3"></div>
          <div className="h-24 bg-slate-200 rounded"></div>
          <div className="h-24 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl shadow-lg p-8 text-white">
        <div className="flex items-center gap-3 mb-2">
          <Bell className="w-6 h-6" />
          <h2 className="text-2xl font-bold">Email Reminders</h2>
        </div>
        <p className="text-slate-300">
          Choose which events and document expirations email you. Reminders are on by default; turn
          off anything you don't want.
        </p>
      </div>

      {visible.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center text-slate-600">
          There are no email reminders available for your role.
        </div>
      ) : (
        categories.map((category) => {
          const items = visible.filter((r) => r.category === category);
          return (
            <div key={category} className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                <h3 className="text-lg font-bold text-slate-900">{category}</h3>
                <div className="flex items-center gap-2 text-sm">
                  <button onClick={() => setCategoryEnabled(category, true)} className="text-emerald-600 hover:text-emerald-700 font-medium">
                    Enable all
                  </button>
                  <span className="text-slate-300">|</span>
                  <button onClick={() => setCategoryEnabled(category, false)} className="text-slate-500 hover:text-slate-700 font-medium">
                    Disable all
                  </button>
                </div>
              </div>

              <div className="divide-y divide-slate-100">
                {items.map((r) => {
                  const p = prefs[r.key] ?? defaultPref(r);
                  const sc = (p.schedule_config ?? {}) as Record<string, unknown>;
                  return (
                    <div key={r.key} className="p-6 flex items-start justify-between gap-6">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-slate-400" />
                          <span className="font-semibold text-slate-900">{r.label}</span>
                        </div>
                        <p className="text-sm text-slate-500 mt-1">{r.description}</p>

                        {p.enabled && r.control === 'expiring' && (
                          <div className="mt-3 flex items-center gap-2 text-sm">
                            <span className="text-slate-600">Notify me</span>
                            <select
                              value={p.lead_days ?? r.defaultLeadDays ?? 30}
                              onChange={(e) => patch(r.key, { lead_days: Number(e.target.value) })}
                              className="px-3 py-1.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                            >
                              {LEAD_DAY_OPTIONS.map((d) => (
                                <option key={d} value={d}>{d} days before</option>
                              ))}
                            </select>
                          </div>
                        )}

                        {p.enabled && r.control === 'weekly' && (
                          <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
                            <span className="text-slate-600">Every</span>
                            <select
                              value={(sc.day as string) ?? 'mon'}
                              onChange={(e) => patch(r.key, { schedule_config: { ...sc, day: e.target.value } })}
                              className="px-3 py-1.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                            >
                              {DOW_OPTIONS.map((d) => (<option key={d.value} value={d.value}>{d.label}</option>))}
                            </select>
                            <span className="text-slate-600">at</span>
                            <select
                              value={(sc.hour as number) ?? 8}
                              onChange={(e) => patch(r.key, { schedule_config: { ...sc, hour: Number(e.target.value) } })}
                              className="px-3 py-1.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                            >
                              {HOUR_OPTIONS.map((h) => (<option key={h} value={h}>{formatHour(h)}</option>))}
                            </select>
                            <span className="text-slate-400 text-xs">(Eastern)</span>
                          </div>
                        )}

                        {p.enabled && r.control === 'monthly' && (
                          <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
                            <span className="text-slate-600">On day</span>
                            <select
                              value={(sc.dom as number) ?? 1}
                              onChange={(e) => patch(r.key, { schedule_config: { ...sc, dom: Number(e.target.value) } })}
                              className="px-3 py-1.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                            >
                              {DOM_OPTIONS.map((d) => (<option key={d} value={d}>{d}</option>))}
                            </select>
                            <span className="text-slate-600">of the month at</span>
                            <select
                              value={(sc.hour as number) ?? 8}
                              onChange={(e) => patch(r.key, { schedule_config: { ...sc, hour: Number(e.target.value) } })}
                              className="px-3 py-1.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                            >
                              {HOUR_OPTIONS.map((h) => (<option key={h} value={h}>{formatHour(h)}</option>))}
                            </select>
                            <span className="text-slate-400 text-xs">(Eastern)</span>
                          </div>
                        )}
                      </div>

                      <label className="relative inline-flex items-center cursor-pointer mt-1">
                        <input
                          type="checkbox"
                          checked={p.enabled}
                          onChange={(e) => patch(r.key, { enabled: e.target.checked })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })
      )}

      <div className="flex justify-end">
        <button
          onClick={save}
          disabled={saving || visible.length === 0}
          className="flex items-center gap-2 px-6 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Saving...' : 'Save Preferences'}
        </button>
      </div>
    </div>
  );
}
